import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Field, Form, getFormValues, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import * as _ from 'lodash';
import { __ } from '../../core/utils';
import Select from '../../components/Select';
import Input from '../../components/Input';
import Textarea from '../../components/Textarea';
import Checkbox from '../../components/Checkbox';
import DateTimePicker from '../../components/DateTimePicker';
import SearchableDropdown from '../../components/SearchableDropdown';
import ArrowDownSmallIcon from '../../assets/images/arrow-down-small-15x15.svg';
import Tooltip from '../../components/Tooltip';
import Button from '../../components/Button';
import classNames from 'classnames';
import { createAddressBookCompany } from '../../redux/actions/table/address_book_companies';
import { listPossibleSubstagesWithChildrenByStage } from '../../redux/actions/table/all_tasks';
import { listPossibleWorkSites } from '../../redux/actions/table/timesheets';
import {
  listPossibleSpecificationProjects,
  listPossibleSpecificationStages,
} from '../../redux/actions/table/working_set_specifications';
import styles from '../../styles/forms/timesheets/EditTimesheetEntryWorkItemForm.scss';

const EMPTY_SELECTION = {
  values: [],
  type: null,
  label: null,
  substage_id: null,
  task_id: null,
};

const normalizeDropdownOptions = (options = []) =>
  _.map(options, (option, index) => ({
    ...option,
    group_option_key:
      option?.group_option_key || `__group__${option?.group_id}`,
    option_key:
      option?.option_key ||
      ['be', option?.group_id, option?.value, option?.label, index].join('_'),
  }));

const buildSelectedState = (options = [], taskId, substageId) => {
  const normalizedOptions = normalizeDropdownOptions(options);

  if (!_.isNil(taskId)) {
    const selectedTask = _.find(normalizedOptions, ['value', taskId]);

    if (selectedTask) {
      return {
        values: [selectedTask.option_key],
        type: 'child',
        label: selectedTask.label || null,
        substage_id: null,
        task_id: taskId,
      };
    }
  }

  if (!_.isNil(substageId)) {
    const selectedGroup = _.find(normalizedOptions, ['group_id', substageId]);

    if (selectedGroup) {
      const groupValues = _.map(
        _.filter(normalizedOptions, ['group_id', substageId]),
        'option_key',
      );

      return {
        values: [selectedGroup.group_option_key, ...groupValues],
        type: 'group',
        label: selectedGroup.group || null,
        substage_id: substageId,
        task_id: null,
      };
    }
  }

  return EMPTY_SELECTION;
};

const EditTimesheetEntryWorkItemForm = (props) => {
  const { values = {}, handleSubmit, change, onClose, onSubmit } = props;
  const isStructureLocked = true;
  const prevProjectId = useRef();
  const prevStageId = useRef();
  const [possibleProjects, setPossibleProjects] = useState([]);
  const [possibleStages, setPossibleStages] = useState([]);
  const [possibleSubstagesWithChildren, setPossibleSubstagesWithChildren] =
    useState([]);
  const [possibleWorkSites, setPossibleWorkSites] = useState([]);
  const [selected, setSelected] = useState(EMPTY_SELECTION);

  useEffect(() => {
    listPossibleSpecificationProjects().then(({ response }) => {
      setPossibleProjects(response.data || []);
    });

    listPossibleWorkSites().then(({ response }) => {
      setPossibleWorkSites(response.data || []);
    });
  }, []);

  useEffect(() => {
    if (values.project_id) {
      listPossibleSpecificationStages(values.project_id).then(
        ({ response }) => {
          setPossibleStages(response.data || []);
        },
      );

      if (
        !_.isNil(prevProjectId.current) &&
        prevProjectId.current !== values.project_id
      ) {
        change('stage_id', null);
        change('task_id', null);
        change('substage_id', null);
        setSelected(EMPTY_SELECTION);
      }

      prevProjectId.current = values.project_id;
      return;
    }

    setPossibleStages([]);
    change('stage_id', null);
    prevProjectId.current = values.project_id;
  }, [change, values.project_id]);

  useEffect(() => {
    if (values.stage_id) {
      listPossibleSubstagesWithChildrenByStage(values.stage_id).then(
        ({ response }) => {
          const options = response.data || [];

          setPossibleSubstagesWithChildren(options);
          setSelected(
            buildSelectedState(options, values.task_id, values.substage_id),
          );
        },
      );

      if (
        !_.isNil(prevStageId.current) &&
        prevStageId.current !== values.stage_id
      ) {
        change('task_id', null);
        change('substage_id', null);
        setSelected(EMPTY_SELECTION);
      }

      prevStageId.current = values.stage_id;
      return;
    }

    setPossibleSubstagesWithChildren([]);
    setSelected(EMPTY_SELECTION);
    change('task_id', null);
    change('substage_id', null);
    prevStageId.current = values.stage_id;
  }, [change, values.stage_id]);

  const dropdownOptions = useMemo(
    () => normalizeDropdownOptions(possibleSubstagesWithChildren),
    [possibleSubstagesWithChildren],
  );

  const handleCreateNewWorksite = (value) => {
    createAddressBookCompany({ name: value, is_office: 1 }).then(
      ({ response }) => {
        listPossibleWorkSites()
          .then(({ response: workSiteResponse }) => {
            setPossibleWorkSites(workSiteResponse.data || []);
          })
          .then(() => {
            change('address_book_company_id', _.get(response.data, 'id'));
          });
      },
    );
  };

  return (
    <Form className={styles.wrapper} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.form}>
        <div className={styles.section}>
          <div className={styles.flex}>
            <div className={styles.f2}>
              <Field
                name='date'
                component={DateTimePicker}
                label={__('table.columns.timesheet-date')}
                footer
                disabled={isStructureLocked}
              />
            </div>
            <div className={styles.f4} />
          </div>

          <div className={styles.flex}>
            <div className={styles.f1}>
              <Field
                name='hours'
                component={Input}
                label={__('table.columns.timesheet-hours')}
                numeric
                maxLength={4}
                hint={__('timesheets.hint.in-hours')}
              />
            </div>
            <div className={styles.f1}>
              <Field
                name='minutes'
                component={Input}
                label={__('table.columns.timesheet-minutes')}
                numeric
                maxLength={2}
                hint={__('timesheets.hint.in-minutes')}
              />
            </div>
            <div className={styles.f2} />
          </div>

          <div className={styles.flex}>
            <div className={styles.f1}>
              <Field
                name='project_id'
                searchable
                component={Select}
                disabled={isStructureLocked}
                label={__('edit-timesheet-form.project')}
                options={_.map(possibleProjects, (project) => ({
                  value: project.id,
                  label: [project.code, project.name].join(': '),
                }))}
                placeholder={__('edit-time-entry-form.select-project')}
              />
            </div>
            <div className={styles.f1}>
              <Field
                name='stage_id'
                searchable
                component={Select}
                disabled={isStructureLocked || !values.project_id}
                label={__('edit-timesheet-form.stage')}
                options={_.map(possibleStages, (stage) => ({
                  value: stage.id,
                  label: [_.padStart(stage.order, 2, '0'), stage.name].join(
                    ': ',
                  ),
                }))}
                placeholder={__('edit-time-entry-form.select-stage')}
              />
            </div>
          </div>

          <div className={styles.searchableDropdownWrapper}>
            <SearchableDropdown
              options={dropdownOptions}
              dropdownWrapperClassName={styles.dropdownWrapper}
              multiselect
              closable={false}
              name='timesheet_entries_edit_work_item_selector'
              leftStyled
              readOnly={isStructureLocked}
              selectedValues={selected.values}
              header={
                <div className={styles.dropdownHeader}>
                  <Tooltip
                    text={__('edit-timesheet-form.task')}
                    className={styles.filterHeaderName}
                  >
                    {__('edit-timesheet-form.task')}
                  </Tooltip>
                  {!isStructureLocked && (
                    <span
                      className={styles.filterHeaderClearAll}
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelected(EMPTY_SELECTION);
                        change('task_id', null);
                        change('substage_id', null);
                      }}
                    >
                      {__('add-timesheet-form.substages-and-tasks.clear')}
                    </span>
                  )}
                </div>
              }
              onSelect={(item, groupLabel) => {
                if (item.groupHeading) {
                  const groupValues = _.map(
                    _.filter(dropdownOptions, (option) =>
                      item.group_id
                        ? option.group_id === item.group_id
                        : option.group === groupLabel,
                    ),
                    'option_key',
                  );

                  setSelected({
                    values: [item.group_option_key, ...groupValues],
                    type: 'group',
                    label: groupLabel || item.group || null,
                    substage_id: item.group_id || null,
                    task_id: null,
                  });
                  change('task_id', null);
                  change('substage_id', item.group_id || null);
                  return;
                }

                setSelected({
                  values: [item.option_key || item.value],
                  type: 'child',
                  label: item.label || null,
                  substage_id: null,
                  task_id: item.value || null,
                });
                change('task_id', item.value || null);
                change('substage_id', null);
              }}
              onRemove={() => {
                setSelected(EMPTY_SELECTION);
                change('task_id', null);
                change('substage_id', null);
              }}
            >
              <div
                className={classNames(
                  styles.bubble,
                  isStructureLocked && styles.bubbleDisabled,
                )}
              >
                <label className={styles.bubbleLabel}>
                  {__('edit-timesheet-form.task')}
                </label>
                <span
                  className={classNames(
                    !selected.label && styles.bubblePlaceholder,
                  )}
                >
                  {selected.label ||
                    __('add-timesheet-form.choose-substage-or-task')}
                </span>
                <ArrowDownSmallIcon className={styles.bubbleArrow} />
              </div>
            </SearchableDropdown>
          </div>

          <Field name='task_id' component='input' type='hidden' />
          <Field name='substage_id' component='input' type='hidden' />

          <Field
            name='address_book_company_id'
            searchable
            component={Select}
            label={__('edit-timesheet-form.work-site')}
            options={_.map(possibleWorkSites, (workSite) => ({
              value: workSite.id,
              label: workSite.name,
            }))}
            callbackSetNewValue={handleCreateNewWorksite}
            placeholder={__('edit-time-entry-form.select-location')}
          />

          <Field
            name='description'
            component={Textarea}
            label={__('edit-timesheet-form.description')}
            maxLength={200}
            rows={1}
            maxRows={5}
            placeholder={__('edit-time-entry-form.add-description')}
          />

          <div className={styles.flex}>
            <div className={styles.f1}>
              <Field
                name='is_billable'
                component={Checkbox}
                label={__('edit-timesheet-form.nonbillable')}
                className={styles.checkbox}
              />
            </div>
          </div>

          <div className={styles.footer}>
            <Button
              lightGray
              medium
              middleText={__('button.cancel')}
              onClick={onClose}
            />
            <Button
              lightBlue
              medium
              middleText={__('button.edit')}
              submit
              disabled={!values.project_id || !values.stage_id}
            />
          </div>
        </div>
      </div>
    </Form>
  );
};

export default reduxForm({
  form: 'timesheet_entries.edit_work_item',
  enableReinitialize: true,
})(
  connect((state) => ({
    values: getFormValues('timesheet_entries.edit_work_item')(state),
  }))(EditTimesheetEntryWorkItemForm),
);
